<template>
  <div
    v-if="hasNav"
    v-waypoint="{
      active: true,
      callback: onWaypoint,
      options: intersectionOptions,
    }"
    class="slice slice--nav d-flex justify-center"
  >
    <nav
      ref="nav"
      class="slice__nav"
      :class="{ 'slice__nav--fixed': fixed, 'fixed-to-bottom': fixedBottom }"
    >
      <div class="container d-flex">
        <div class="d-flex justify-center flex-grow-1">
          <div class="slice__nav__inner d-flex">
            <a
              v-for="link in links"
              ref="links"
              :key="link.to"
              :href="`#${link.to}`"
              class="slice__nav__link link--underline"
              :class="linkClass(link)"
              @click.prevent="goTo($event, link.to, link.offsetY)"
            >
              <span>{{ link.text }}</span>
            </a>
          </div>

          <div v-if="overflowLinks.length" class="slice__nav__other d-flex">
            <a
              ref="otherLink"
              href="#"
              class="slice__nav__link link--underline"
              :class="{ open: otherOpen }"
              @click.prevent="toggleOther($event)"
            >
              <span>{{ $t('product.nav.other') }}</span>
              <icon-carret-down />
            </a>

            <transition name="fade-from-top">
              <div
                v-if="otherOpen"
                ref="otherMenu"
                class="slice__nav__overflow-links"
              >
                <a
                  v-for="link in overflowLinks"
                  :key="link.to"
                  :href="`#${link.to}`"
                  class="slice__nav__link link--underline"
                  :class="{
                    'slice__nav__link--current': isCurrent(link.current),
                  }"
                  @click.prevent="goTo($event, link.to, link.offsetY)"
                >
                  <span>{{ link.text }}</span>
                </a>
              </div>
            </transition>
          </div>

          <transition name="fade">
            <div
              v-if="displayAddToCartBtn"
              class="slice__nav__button d-flex align-center pl-1-5"
            >
              <primary-button
                :text="actionText"
                align="center"
                @click.native="goTo($event, 'header')"
              />
            </div>
          </transition>
        </div>
      </div>
    </nav>
  </div>
</template>

<script>
import { mapMutations, mapActions } from 'vuex'
import PrimaryButton from '@/components/buttons/Primary'
import IconCarretDown from '@/components/icons/CarretDown'
import { isProductSaleable } from '~/entities/sanity/product'

export default {
  name: 'SliceNav',
  components: {
    PrimaryButton,
    IconCarretDown,
  },
  props: {
    blocks: {
      type: Array,
      required: true,
    },
    blocksInScreen: {
      type: Array,
      default: () => [],
    },
    productDocument: {
      type: Object,
      required: true,
    },
  },
  data: () => ({
    intersectionOptions: {
      root: null,
      rootMargin: '0px 0px 0px 0px',
      threshold: [0, 1],
    },
    fixed: false,
    fixedBottom: false,
    otherOpen: false,
    overflowLinks: [],
    offset30: [
      'video',
      'title',
      'simple_content',
      'morpho',
      'tracabilite',
      'impacts',
    ],
  }),
  computed: {
    links() {
      const ll = []

      ll.push({
        name: 'purchase',
        text: this.$t('product.nav.product'),
        to: 'header',
        offsetY: 0,
        current: [0],
      })

      let lastIsDesc = false
      const blockTypeInNav = []
      for (const block of this.blocks) {
        if (
          lastIsDesc &&
          ['simpleContentBlock', 'titleBlock', 'imageFullBlock'].includes(
            block._type
          )
        ) {
          continue
        } else if (['deliveryBlock'].includes(block._type)) {
          continue
        } else if (['productNavBlock', 'titleBlock'].includes(block._type)) {
          lastIsDesc = false
          continue
        } else if (block._type === 'simpleContentBlock') {
          ll.push(
            this.getFirstSliceLinkByType('simpleContentBlock', true, true)
          )
          lastIsDesc = true
          blockTypeInNav.push('simpleContentBlock')
        } else if (!blockTypeInNav.includes(block._type)) {
          ll.push(this.getFirstSliceLinkByType(block._type))
          lastIsDesc = false
          blockTypeInNav.push(block._type)
        }
      }

      return ll
    },
    actionText() {
      return this.$t(`product.nav.action.${this.productDocument.status}`)
    },
    hasNav() {
      return this.links.length > 1
    },
    displayAddToCartBtn() {
      return (
        this.fixed &&
        !this.fixedBottom &&
        isProductSaleable(this.productDocument)
      )
    },
  },
  mounted() {
    this.organiseLinks()

    window.addEventListener('resize', this.resize)
  },
  beforeDestroy() {
    document.body.removeEventListener('click', this.clickBody)
    window.removeEventListener('resize', this.resize)
  },
  methods: {
    ...mapMutations({
      setScroll: 'behavior/SET_SCROLL',
      addObjectToClose: 'behavior/ADD_OBJECT_TO_CLOSE',
      removeObjectToClose: 'behavior/CLEAN_OBJECT_TO_CLOSE',
    }),
    ...mapActions({
      closeOtherMenus: 'behavior/closeObjects',
    }),
    getFirstSliceLinkByType(
      name,
      concernMultipleSlice = false,
      canHaveTitleBefore = false
    ) {
      let cursor = this.blocks.findIndex((b) => b._type === name)

      let end
      if (concernMultipleSlice) {
        let index = cursor + 1
        while (this.blocks[index] && this.blocks[index]._type === name) {
          end = index
          index++
        }
      }

      let withTitle = false
      if (canHaveTitleBefore) {
        if (this.blocks[cursor - 1]._type === 'titleBlock') {
          withTitle = this.blocks[cursor - 1]._key
          cursor--
        }
      }

      return {
        text: this.$t(`product.nav.${name}`),
        to: withTitle || this.blocks[cursor]._key,
        offsetY: this.offset30.includes(name) ? 30 : 0,
        current: concernMultipleSlice ? [cursor, end] : [cursor],
        name,
      }
    },
    onWaypoint({ el }) {
      const h = document.getElementById('header')
      if (h) {
        const header = h.firstChild
        const screenY = window.scrollY - header.offsetHeight
        const screenYBottom = screenY + window.innerHeight

        if (window.innerWidth > 769) {
          this.fixed = screenY > el.offsetTop || screenYBottom < el.offsetTop
          this.fixedBottom = screenYBottom < el.offsetTop
        } else {
          this.fixed = screenY > el.offsetTop
        }

        this.$nextTick().then((_) => {
          this.organiseLinks()
        })
      }
    },
    organiseLinks() {
      const ll = []

      if (this.$refs.nav) {
        const nav = this.$refs.nav

        for (const [index, link] of Object.entries(this.$refs.links)) {
          if (this.fixed) {
            if (link.offsetTop > 0) {
              ll.push(this.links[index])
            }
          } else if (link.offsetTop > nav.offsetTop) {
            ll.push(this.links[index])
          }
        }

        if (this.overflowLinks.length === 0 && ll.length > 0) {
          this.overflowLinks = ll

          this.$nextTick().then((_) => {
            this.organiseLinks()
          })

          return
        }

        if (this.overflowLinks.length !== ll.length) {
          this.overflowLinks = ll
        }

        this.$nextTick().then((_) => {
          if (window.innerWidth <= 769) {
            this.centerCurrentLink()
          }
        })
      }
    },
    toggleOther(ev) {
      ev.stopPropagation()
      this.otherOpen = !this.otherOpen

      if (this.otherOpen) {
        this.closeOtherMenus()
        document.body.addEventListener('click', this.clickBody)
        this.addObjectToClose(() => {
          this.close()
        })
      } else {
        document.body.removeEventListener('click', this.clickBody)
        this.removeObjectToClose()
      }

      this.$refs.otherLink.blur()

      return false
    },
    close() {
      this.otherOpen = false
      document.body.removeEventListener('click', this.clickBody)
      this.removeObjectToClose()
    },
    isCurrent(limits) {
      if (limits.length === 2) {
        return (
          this.blocksInScreen[0] >= limits[0] &&
          this.blocksInScreen[0] <= limits[1]
        )
      }

      return this.blocksInScreen.includes(limits[0])
    },
    centerCurrentLink() {
      const link = this.$refs.links.find((l) =>
        l.classList.contains('slice__nav__link--current')
      )

      if (this.fixed && link && link.scrollIntoView) {
        link.scrollIntoView()
      }
    },
    linkClass(link) {
      const cc = [`slice__nav__link--${link.name}`]

      if (this.isCurrent(link.current)) {
        cc.push('slice__nav__link--current')
      }

      return cc
    },
    clickBody(ev) {
      const cart = this.$refs.otherMenu

      if (!cart.contains(ev.target)) {
        this.close()
      }
    },
    resize() {
      this.organiseLinks()
    },
    async goTo(ev, id, offsetY = 0) {
      const { gsap } = await import('gsap' /* webpackChunkName: 'gsap' */)
      const { ScrollToPlugin } = await import(
        'gsap/ScrollToPlugin' /* webpackChunkName: 'gsap_scroll_to_plugin' */
      )
      gsap.registerPlugin(ScrollToPlugin)

      offsetY += this.$refs.nav.offsetHeight

      if (document.body.classList.contains('header-visible')) {
        offsetY += document.getElementById('header').firstChild.offsetHeight
      }

      const target = document.getElementById(id)
      if (!target) {
        console.warn(`could scroll to ${id} because it doesn't exist`)
        return
      }

      this.setScroll(true)
      gsap.to(window, {
        duration: 0.5,
        scrollTo: { y: target, offsetY },
        onComplete: () => {
          this.setScroll(false)
        },
      })

      this.$track.click('slice_nav', {
        id,
      })
      ev.target.blur()
    },
  },
}
</script>

<style lang="scss">
.slice--nav {
  --nav-height: #{rem(50px)};

  @include mq($from: tablet) {
    --nav-height: #{rem(58px)};
  }

  height: var(--nav-height);
}

.slice__nav {
  background: var(--tertiary-color);
  height: var(--nav-height);
  border-bottom: rem(2px) solid var(--secondary-color-4);
  width: 100%;

  @include mq($until: tablet) {
    overflow: hidden;
  }

  .container {
    padding: 0;

    @include mq($until: tablet) {
      overflow: auto;
    }
  }

  &__inner {
    height: var(--nav-height);

    @include mq($from: tablet) {
      overflow: hidden;
      flex-wrap: wrap;
    }
  }

  &__other {
    position: relative;

    @include mq($until: tablet) {
      display: none;
    }

    .icon {
      width: rem(20px);
      height: rem(20px);
    }

    .slice__nav__link.open {
      .icon {
        transform: rotate(180deg);
      }
    }
  }

  &__link {
    display: flex;
    align-items: center;
    padding: 0 rem($spacing);
    position: relative;
    line-height: 1.2;
    height: var(--nav-height);
    font-size: var(--small-font-size);
    white-space: nowrap;

    @include mq($from: tablet) {
      padding: 0 rem($spacing * 1.5);
      font-size: rem($base-font-size);
    }

    &::after {
      content: '';
      position: absolute;
      bottom: rem(1);
      left: rem($spacing);
      right: rem($spacing);
      height: rem(3px);
      background: var(--secondary-color);
      transform: scale(0);
      transition: 0.3s;

      @include mq($from: tablet) {
        bottom: 0;
      }
    }

    &--current {
      font-weight: 600;

      &::after {
        transform: scale(1);
      }

      span {
        &::after {
          content: none;
        }
      }
    }

    &:first-child {
      .slice__nav__link--current {
        left: rem(-$spacing * 0.5);
      }
    }
  }

  &__overflow-links {
    position: absolute;
    top: 100%;
    right: 0;
    background: var(--tertiary-color);
    margin-top: rem(-10px);
    border: 2px solid var(--secondary-color-4);
    padding: rem($spacing * 0.8) 0;

    .fixed-to-bottom & {
      top: auto;
      bottom: 100%;
    }

    .slice__nav__link {
      white-space: nowrap;
      padding: rem($spacing * 0.8) rem($spacing * 1.6);
      height: auto;

      &::after {
        top: 0;
        left: 0;
        right: auto;
        height: auto;
        width: rem(4px);
      }
    }
  }

  &__button {
    margin-left: auto;
  }

  .action {
    padding-top: 0;
    padding-bottom: 0;
    height: 100%;
    box-shadow: none;
    min-width: rem(150px);

    &__inner {
      min-height: 100%;
    }
  }

  &--fixed {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    transition: margin 0.4s cubic-bezier(0.46, 0.01, 0.32, 1);
    z-index: 900;

    .header-visible & {
      margin-top: rem(50px);

      @include mq($from: tablet) {
        margin-top: rem(76px);
      }
    }

    &.fixed-to-bottom {
      top: auto;
      bottom: 0;
      margin-top: 0;
      border-bottom: 0;
      border-top: rem(2px) solid var(--secondary-color-4);
      border-right: rem(1px) solid var(--secondary-color-4);
    }
  }
}
</style>
